import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Download, ReceiptText, FileText, ClipboardList, FolderDown, ChevronDown, FileUp, Radio, CalendarPlus, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

const STATUS_STYLE = {
  paid: "bg-emerald-50 text-emerald-700",
  unpaid: "bg-amber-50 text-amber-700",
  overdue: "bg-red-50 text-[#D5281B]",
  draft: "bg-slate-100 text-slate-600",
  sent: "bg-sky-50 text-sky-700",
};

const inputCls = "w-full h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e7d8c] bg-white";
const labelCls = "text-xs font-semibold text-slate-600 block mb-1";

const EMPTY_BOOKING = {
  company_name: "", telephone: "", date_of_service: "", service_location: "",
  start_time: "", end_time: "", participants: "", sites: "", overnight: "", max_capacity: "",
  alcohol: "", security: "", parking: "", freshwater: "", power: "", additional_info: "",
  extra_dates: [],
};

function statusOf(inv) {
  if (inv.status === "paid") return "paid";
  if (inv.kind !== "quote" && inv.due_date && inv.due_date < new Date().toISOString().slice(0, 10)) return "overdue";
  return inv.status || "unpaid";
}

function InvoiceCard({ inv }) {
  const [open, setOpen] = useState(false);
  const st = statusOf(inv);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`portal-invoice-${inv.id}`}>
      <div onClick={() => setOpen((o) => !o)} className="w-full px-4 py-3.5 flex items-center gap-3 text-left cursor-pointer">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#1c3d49]">{inv.invoice_no} <span className="font-normal text-slate-500">· {inv.event_name}</span></p>
          <p className="text-xs text-slate-400 mt-0.5">
            {inv.event_date && `Event: ${inv.event_date} · `}Due: {inv.due_date || "—"}
          </p>
        </div>
        <span className="text-sm font-bold text-[#1c3d49]">{gbp(inv.total)}</span>
        <span className={`text-[10px] font-bold px-2 py-1 rounded-full capitalize ${STATUS_STYLE[st]}`} data-testid={`portal-invoice-status-${inv.id}`}>
          {st === "paid" && inv.paid_at ? `Paid ${new Date(inv.paid_at).toLocaleDateString("en-GB")}` : st}
        </span>
        <a href={`${API}/api/client-portal/invoices/${inv.id}/pdf`} onClick={(e) => e.stopPropagation()}
          data-testid={`portal-invoice-download-${inv.id}`} title="Download PDF"
          className="h-9 w-9 grid place-items-center rounded-lg border border-slate-200 text-slate-500 hover:border-[#1e7d8c] hover:text-[#1e7d8c] transition-colors shrink-0">
          <Download className="h-4 w-4" />
        </a>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3" data-testid={`portal-invoice-breakdown-${inv.id}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                <th className="pb-1.5">Description</th>
                <th className="pb-1.5 text-right w-14">Qty</th>
                <th className="pb-1.5 text-right w-20">Unit</th>
                <th className="pb-1.5 text-right w-24">Total</th>
              </tr>
            </thead>
            <tbody>
              {inv.items.map((it, i) => (
                <tr key={i} className="border-t border-slate-50">
                  <td className="py-1.5 pr-2">{it.description}</td>
                  <td className="py-1.5 text-right">{it.qty}</td>
                  <td className="py-1.5 text-right">{gbp(it.unit_price)}</td>
                  <td className="py-1.5 text-right font-medium">{gbp(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 pt-2 border-t border-slate-100 text-sm space-y-0.5 text-right">
            <p className="text-slate-500">Subtotal: {gbp(inv.subtotal)}</p>
            {inv.discount_amount > 0 && (
              <p className="text-emerald-700">Discount{inv.discount_type === "percent" ? ` (${inv.discount_value}%)` : ""}: −{gbp(inv.discount_amount)}</p>
            )}
            <p className="font-bold text-[#1c3d49] text-base">Total: {gbp(inv.total)}</p>
            {(inv.deposit_total || 0) > 0 && (
              <>
                <p className="text-emerald-700 font-semibold" data-testid={`portal-deposit-${inv.id}`}>Deposit paid: −{gbp(inv.deposit_total)}</p>
                <p className="font-bold text-[#1c3d49] text-base">Balance left to pay: {gbp(inv.balance_due)}</p>
              </>
            )}
          </div>
          {inv.notes && <p className="text-xs text-slate-500 mt-2 whitespace-pre-wrap">{inv.notes}</p>}
        </div>
      )}
    </div>
  );
}

export default function PortalHome() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [reports, setReports] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [liveViews, setLiveViews] = useState([]);
  const [upTitle, setUpTitle] = useState("");
  const [upFile, setUpFile] = useState(null);
  const [upBusy, setUpBusy] = useState(false);
  const [booking, setBooking] = useState({ ...EMPTY_BOOKING });
  const [bookBusy, setBookBusy] = useState(false);
  const [bookDone, setBookDone] = useState(false);
  const [quick, setQuick] = useState({ event_date: "", start_time: "", end_time: "", title: "" });
  const [quickBusy, setQuickBusy] = useState(false);
  const [myEvents, setMyEvents] = useState([]);

  const track = useCallback((action, detail = "") => {
    fetch(`${API}/api/client-portal/track`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, detail }),
    }).catch((e) => console.debug("Portal activity tracking failed:", e?.message || e));
  }, []);

  const call = useCallback(async (path) => {
    const r = await fetch(`${API}/api${path}`, { credentials: "include" });
    if (r.status === 401) {
      navigate("/portal/login");
      throw new Error("unauthenticated");
    }
    if (!r.ok) throw new Error("load failed");
    return r.json();
  }, [navigate]);

  const loadDocs = useCallback(() => call("/client-portal/documents").then(setDocuments).catch((e) => console.debug("Portal documents load failed:", e?.message || e)), [call]);

  useEffect(() => {
    (async () => {
      try {
        const meData = await call("/client-portal/me");
        setMe(meData);
        setBooking((b) => ({ ...b, company_name: meData.client_name, service_location: b.service_location || meData.usual_venue || "" }));
        const [inv, quo, rep, live] = await Promise.all([
          call("/client-portal/invoices"), call("/client-portal/quotes"),
          call("/client-portal/reports"), call("/client-portal/live-views"),
        ]);
        setInvoices(inv); setQuotes(quo); setReports(rep); setLiveViews(live);
        loadDocs();
        call("/client-portal/my-events").then(setMyEvents).catch(() => {});
      } catch (e) { /* redirect handled in call() */ }
    })();
  }, [call, loadDocs]);

  const changeTab = (key, label) => {
    setTab(key);
    track("Viewed", label);
  };

  const logout = async () => {
    await fetch(`${API}/api/client-portal/logout`, { method: "POST", credentials: "include" });
    navigate("/portal/login");
  };

  const uploadDoc = async () => {
    if (!upFile) return;
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", upFile);
      const r = await fetch(`${API}/api/client-portal/documents?title=${encodeURIComponent(upTitle)}`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Upload failed — try again");
      toast.success("Sent — Wales Event Medical Services can see it now");
      track("Uploaded a document", upTitle || upFile.name);
      setUpTitle("");
      setUpFile(null);
      loadDocs();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpBusy(false);
    }
  };

  const setB = (k) => (e) => setBooking((b) => ({ ...b, [k]: e.target.value }));

  const quickBook = async () => {
    setQuickBusy(true);
    try {
      const r = await fetch(`${API}/api/client-portal/quick-book`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(quick),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Couldn't book — try again");
      toast.success(data.message);
      track("Quick-booked an event", `${quick.event_date} ${quick.title}`.trim());
      setQuick({ event_date: "", start_time: "", end_time: "", title: "" });
      setMyEvents(await call("/client-portal/my-events"));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setQuickBusy(false);
    }
  };

  const yesNo = (k, label, testid) => (
    <label className="block" key={k}>
      <span className={labelCls}>{label}</span>
      <select value={booking[k]} onChange={setB(k)} data-testid={testid} className={inputCls}>
        <option value="">Select…</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </label>
  );

  const submitBooking = async (e) => {
    e.preventDefault();
    setBookBusy(true);
    try {
      const r = await fetch(`${API}/api/client-portal/booking`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...booking, extra_dates: (booking.extra_dates || []).filter((d) => d.date) }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Couldn't send — try again");
      setBookDone(true);
      setBooking({ ...EMPTY_BOOKING, company_name: me.client_name, service_location: me.usual_venue || "" });
      toast.success(data.message);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBookBusy(false);
    }
  };

  if (!me) {
    return <div className="min-h-screen bg-[#F4F7FA] grid place-items-center text-slate-500 text-sm">Loading…</div>;
  }

  const TABS = [
    ["invoices", `Invoices (${invoices.length})`, ReceiptText],
    ["quotes", `Quotes (${quotes.length})`, FileText],
    ["reports", `Event reports (${reports.length})`, ClipboardList],
    ["live", `Live view (${liveViews.length})`, Radio],
    ["documents", `Documents (${documents.length})`, FolderDown],
    ["book", "Book another event", CalendarPlus],
  ];

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="client-portal">
      <header className="bg-[#142129] text-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <img src="/site-assets/logo.png" alt="Wales Event Medical Services" className="h-11 w-auto bg-white rounded p-1" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base truncate" data-testid="portal-client-name">{me.client_name}</p>
            <p className="text-xs text-slate-400">Client portal · {me.email}</p>
          </div>
          <button onClick={logout} data-testid="portal-logout"
            className="h-10 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold inline-flex items-center gap-1.5 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex gap-2 flex-wrap" data-testid="portal-tabs">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => changeTab(key, label)} data-testid={`portal-tab-${key}`}
              className={`h-10 px-4 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${tab === key ? "bg-[#1e7d8c] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#1e7d8c]"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-4 space-y-3">
          {tab === "invoices" && (
            <>
              <p className="text-xs text-slate-400">Invoices older than 6 months are removed from this view automatically.</p>
              {invoices.length === 0 ? (
                <p className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center" data-testid="portal-invoices-empty">No invoices from the last 6 months.</p>
              ) : invoices.map((inv) => <InvoiceCard key={inv.id} inv={inv} />)}
            </>
          )}

          {tab === "quotes" && (
            quotes.length === 0 ? (
              <p className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center" data-testid="portal-quotes-empty">No quotes from the last 6 months.</p>
            ) : quotes.map((q) => <InvoiceCard key={q.id} inv={q} />)
          )}

          {tab === "reports" && (
            reports.length === 0 ? (
              <p className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center" data-testid="portal-reports-empty">No event reports yet — they appear here automatically after your events.</p>
            ) : reports.map((r) => (
              <div key={r.event_id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`portal-report-${r.event_id}`}>
                <p className="text-sm font-bold text-[#1c3d49]">{r.event_name}</p>
                <p className="text-xs text-slate-400 mt-0.5">{[r.event_date, r.venue].filter(Boolean).join(" · ")}</p>
                <div className="flex gap-2 flex-wrap mt-3">
                  {Object.entries(r.counts || {}).map(([k, v]) => (
                    <span key={k} className="text-xs font-semibold bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full capitalize">
                      {k.replace(/_/g, " ")}: {v}
                    </span>
                  ))}
                </div>
                {(r.patients || []).length > 0 && (
                  <table className="w-full text-sm mt-3">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                        <th className="pb-1.5">Patient</th>
                        <th className="pb-1.5">Injury / complaint</th>
                        <th className="pb-1.5 w-40">Outcome</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.patients.map((p, i) => (
                        <tr key={p.id || i} className="border-t border-slate-50">
                          <td className="py-1.5 pr-2">{[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}</td>
                          <td className="py-1.5 pr-2">{(p.injuries || []).join(", ") || p.complaint || "—"}</td>
                          <td className="py-1.5 capitalize">{(p.outcome || p.status || "—").replace(/_/g, " ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))
          )}

          {tab === "live" && (
            liveViews.length === 0 ? (
              <p className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center" data-testid="portal-live-empty">No live event views yet — on the day of your event, open it here to watch casualties and lost children live.</p>
            ) : (
              <>
                <p className="text-xs text-slate-400">Live during your event — how many people are with the medical team, plus lost children, updating as it happens.</p>
                {liveViews.map((lv) => (
                  <div key={lv.event_id} className="bg-white rounded-xl border border-slate-200 px-4 py-3.5 flex items-center gap-3" data-testid={`portal-live-${lv.event_id}`}>
                    <Radio className="h-5 w-5 text-[#1e7d8c] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#1c3d49] truncate">{lv.event_name}</p>
                      <p className="text-xs text-slate-400">{[lv.event_date, lv.venue].filter(Boolean).join(" · ")}</p>
                    </div>
                    <a href={lv.live_url} target="_blank" rel="noreferrer" data-testid={`portal-live-open-${lv.event_id}`}
                      className="h-10 px-4 rounded-lg bg-[#1e7d8c] hover:bg-[#176472] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                      <ExternalLink className="h-3.5 w-3.5" /> Open live view
                    </a>
                  </div>
                ))}
              </>
            )
          )}

          {tab === "documents" && (
            <>
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="portal-upload-box">
                <p className="text-sm font-bold text-[#1c3d49] mb-1">Send us a document</p>
                <p className="text-xs text-slate-400 mb-3">Event details, site plans, schedules — anything we should have. PDF, images, Word, Excel or CSV, up to 15 MB.</p>
                <div className="flex gap-2 flex-wrap items-center">
                  <input value={upTitle} onChange={(e) => setUpTitle(e.target.value)}
                    placeholder="What is it? (optional)" data-testid="portal-upload-title"
                    className="h-11 flex-1 min-w-40 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e7d8c]" />
                  <label className="h-11 px-4 rounded-lg border border-dashed border-slate-300 text-sm font-semibold text-slate-500 hover:border-[#1e7d8c] hover:text-[#1e7d8c] inline-flex items-center gap-1.5 cursor-pointer transition-colors">
                    <FileUp className="h-4 w-4" /> {upFile ? upFile.name : "Choose file"}
                    <input type="file" className="hidden" data-testid="portal-upload-file"
                      onChange={(e) => setUpFile(e.target.files?.[0] || null)} />
                  </label>
                  <button onClick={uploadDoc} disabled={upBusy || !upFile} data-testid="portal-upload-submit"
                    className="h-11 px-5 rounded-lg bg-[#1e7d8c] hover:bg-[#176472] disabled:opacity-50 text-white text-sm font-semibold transition-colors">
                    {upBusy ? "Sending…" : "Send"}
                  </button>
                </div>
              </div>
              {documents.length === 0 ? (
                <p className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center" data-testid="portal-documents-empty">No documents yet — medical plans and files from us appear here automatically.</p>
              ) : documents.map((d) => (
                <div key={`${d.kind}-${d.id}`} className="bg-white rounded-xl border border-slate-200 px-4 py-3.5 flex items-center gap-3"
                  data-testid={`portal-doc-${d.id}`}>
                  <FileText className="h-5 w-5 text-[#1e7d8c] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1c3d49] truncate">{d.title}</p>
                    <p className="text-xs text-slate-400">
                      {d.kind === "medical-plan" ? "Medical plan (PDF)" : d.filename}
                      {d.created_at && ` · ${new Date(d.created_at).toLocaleDateString("en-GB")}`}
                      {d.direction === "from_client" && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold">Sent by you</span>}
                    </p>
                  </div>
                  <a href={`${API}/api/client-portal/documents/${d.id}/download`}
                    onClick={() => track("Downloaded a document", d.title)} data-testid={`portal-doc-download-${d.id}`}
                    className="h-10 px-4 rounded-lg bg-[#1e7d8c] hover:bg-[#176472] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                </div>
              ))}
            </>
          )}

          {tab === "book" && (
            <>
              <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5" data-testid="portal-quickbook">
                <p className="text-sm font-bold text-[#1c3d49]">Quick book — for your regular events</p>
                <p className="text-xs text-slate-400 mb-3">Just pick the date and times — no form needed. It goes straight onto our calendar and we'll confirm cover.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <input type="date" value={quick.event_date} onChange={(e) => setQuick((q) => ({ ...q, event_date: e.target.value }))} data-testid="quickbook-date" className={inputCls} />
                  <input type="time" value={quick.start_time} onChange={(e) => setQuick((q) => ({ ...q, start_time: e.target.value }))} data-testid="quickbook-start" className={inputCls} title="Start time" />
                  <input type="time" value={quick.end_time} onChange={(e) => setQuick((q) => ({ ...q, end_time: e.target.value }))} data-testid="quickbook-end" className={inputCls} title="Finish time" />
                  <input value={quick.title} onChange={(e) => setQuick((q) => ({ ...q, title: e.target.value }))} placeholder="Title (optional)" data-testid="quickbook-title" className={inputCls} />
                </div>
                <button onClick={quickBook} disabled={quickBusy || !quick.event_date} data-testid="quickbook-submit"
                  className="mt-3 h-11 px-5 rounded-lg bg-[#1e7d8c] hover:bg-[#176472] disabled:opacity-50 text-white text-sm font-bold transition-colors">
                  {quickBusy ? "Booking…" : "Book it in"}
                </button>
              </div>
              {myEvents.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="portal-my-events">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Your booked events</p>
                  <div className="space-y-1.5">
                    {myEvents.map((ev) => (
                      <div key={ev.id} className="flex items-center gap-2 text-sm" data-testid={`portal-my-event-${ev.id}`}>
                        <span className="font-semibold text-[#1c3d49] flex-1 truncate">{ev.name}</span>
                        <span className="text-xs text-slate-400 shrink-0">{ev.event_date}{ev.times ? ` · ${ev.times}` : ""}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${ev.booking_status === "provisional" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {ev.booking_status === "provisional" ? "Provisional" : "Confirmed"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "book" && (
            bookDone ? (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="portal-book-success">
                <CalendarPlus className="h-10 w-10 text-[#1e7d8c] mx-auto mb-3" />
                <p className="text-lg font-bold text-[#1c3d49]">Details sent — thank you</p>
                <p className="text-sm text-slate-500 mt-1">We do need to check availability to make sure we can cover your event. We'll be in contact with you shortly.</p>
                <button onClick={() => setBookDone(false)} data-testid="portal-book-again"
                  className="mt-4 h-10 px-4 rounded-lg border border-slate-300 text-slate-600 text-xs font-bold hover:border-[#1e7d8c] transition-colors">
                  Book another
                </button>
              </div>
            ) : (
              <form onSubmit={submitBooking} className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 space-y-3" data-testid="portal-booking-form">
                <p className="text-sm font-bold text-[#1c3d49]">Book another event with us</p>
                <p className="text-xs text-slate-400">Give us the basics and we'll come back with a quote.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className={labelCls}>Event / company name</span>
                    <input value={booking.company_name} onChange={setB("company_name")} data-testid="book-company" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Contact telephone</span>
                    <input value={booking.telephone} onChange={setB("telephone")} data-testid="book-telephone" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Date of event *</span>
                    <input type="date" required value={booking.date_of_service} onChange={setB("date_of_service")} data-testid="book-date" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Event location *</span>
                    <input required value={booking.service_location} onChange={setB("service_location")} placeholder="Venue / address" data-testid="book-location" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Start time</span>
                    <input type="time" value={booking.start_time} onChange={setB("start_time")} data-testid="book-start" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Finish time</span>
                    <input type="time" value={booking.end_time} onChange={setB("end_time")} data-testid="book-end" className={inputCls} />
                  </label>
                  <div className="sm:col-span-2" data-testid="book-extra-dates">
                    {booking.extra_dates.map((d, i) => (
                      <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-end mb-2">
                        <label className="block">
                          <span className={labelCls}>Additional date {i + 1}</span>
                          <input type="date" value={d.date} data-testid={`book-extra-date-${i}`}
                            onChange={(e) => setBooking((b) => ({ ...b, extra_dates: b.extra_dates.map((x, j) => j === i ? { ...x, date: e.target.value } : x) }))}
                            className={inputCls} />
                        </label>
                        <input type="time" value={d.start_time} title="Start" data-testid={`book-extra-start-${i}`}
                          onChange={(e) => setBooking((b) => ({ ...b, extra_dates: b.extra_dates.map((x, j) => j === i ? { ...x, start_time: e.target.value } : x) }))}
                          className={inputCls} />
                        <input type="time" value={d.end_time} title="Finish" data-testid={`book-extra-end-${i}`}
                          onChange={(e) => setBooking((b) => ({ ...b, extra_dates: b.extra_dates.map((x, j) => j === i ? { ...x, end_time: e.target.value } : x) }))}
                          className={inputCls} />
                        <button type="button" onClick={() => setBooking((b) => ({ ...b, extra_dates: b.extra_dates.filter((_, j) => j !== i) }))}
                          data-testid={`book-extra-remove-${i}`} aria-label="Remove this date"
                          className="h-11 w-11 grid place-items-center rounded-lg border border-slate-300 text-slate-400 hover:text-[#D5281B] hover:border-[#D5281B] transition-colors font-bold">×</button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setBooking((b) => ({ ...b, extra_dates: [...b.extra_dates, { date: "", start_time: "", end_time: "" }] }))}
                      data-testid="book-add-date"
                      className="h-10 px-4 rounded-lg border-2 border-dashed border-slate-300 text-xs font-bold text-[#1e7d8c] hover:border-[#1e7d8c] transition-colors">
                      ＋ Additional date (same venue)
                    </button>
                  </div>
                  <label className="block">
                    <span className={labelCls}>Number of participants</span>
                    <input type="number" min="0" value={booking.participants} onChange={setB("participants")} data-testid="book-participants" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Maximum public capacity</span>
                    <input type="number" min="0" value={booking.max_capacity} onChange={setB("max_capacity")} data-testid="book-capacity" className={inputCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Number of sites covered</span>
                    <input type="number" min="0" value={booking.sites} onChange={setB("sites")} data-testid="book-sites" className={inputCls} />
                  </label>
                  {yesNo("overnight", "Overnight cover needed?", "book-overnight")}
                  {yesNo("alcohol", "Alcohol present?", "book-alcohol")}
                  {yesNo("security", "Security present?", "book-security")}
                  {yesNo("parking", "Parking available?", "book-parking")}
                  {yesNo("freshwater", "Fresh water available?", "book-freshwater")}
                  {yesNo("power", "Power supplied?", "book-power")}
                </div>
                <label className="block">
                  <span className={labelCls}>Additional information</span>
                  <textarea rows={3} value={booking.additional_info} onChange={setB("additional_info")} data-testid="book-additional"
                    placeholder="Previous medical resources, anything else we should know"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e7d8c]" />
                </label>
                <button type="submit" disabled={bookBusy} data-testid="book-submit"
                  className="h-12 px-6 rounded-xl bg-[#1e7d8c] hover:bg-[#176472] disabled:opacity-50 text-white text-sm font-bold transition-colors">
                  {bookBusy ? "Sending…" : "Request a quote"}
                </button>
              </form>
            )
          )}
        </div>
      </div>
    </div>
  );
}
